import { roundToDecimalPlaces } from "utils/numberUtility";

// Shared value coercion for rule evaluation. All cell values arrive as normalised strings
// (see validationTypes.CellValue); rules that need numbers/dates parse here so every rule
// type agrees on what counts as a number or a date.

export const normalise = (value: string) => value.trim().toLowerCase();

export const isBlank = (value: string) => value.trim() === "";

const TOLERANCE_DECIMAL_PLACES = 4;

export function isWithinTolerance(actual: number, expected: number, tolerance: number): boolean {
  return roundToDecimalPlaces(Math.abs(actual - expected), TOLERANCE_DECIMAL_PLACES) <= tolerance;
}

// Strict decimal: optional sign, digits, optional fraction. No thousands separators —
// output CSVs use plain period decimals and Excel numeric cells arrive as
// plain strings already.
const NUMBER_PATTERN = /^[+-]?(\d+(\.\d+)?|\.\d+)$/;

export function parseNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!NUMBER_PATTERN.test(trimmed)) return null;
  return Number(trimmed);
}

export function normaliseNumber(value: string): string | null {
  const trimmed = value.trim();
  if (!NUMBER_PATTERN.test(trimmed)) return null;
  const [whole, fraction = ""] = trimmed.replace(/^[+-]/, "").split(".");
  const wholeDigits = whole.replace(/^0+/, "") || "0";
  const fractionDigits = fraction.replace(/0+$/, "");
  const digits = fractionDigits ? `${wholeDigits}.${fractionDigits}` : wholeDigits;
  return trimmed.startsWith("-") && digits !== "0" ? `-${digits}` : digits;
}

export function normaliseForMatching(value: string, numeric: boolean): string {
  return (numeric ? normaliseNumber(value) : null) ?? normalise(value);
}

/**
 * Reads a share that may be written either way round: "0.25" and "1/4" both give 0.25.
 * A fraction over zero has no value, so it is rejected like any other unreadable cell.
 */
export function parseFraction(value: string): number | null {
  const plain = parseNumber(value);
  if (plain !== null) return plain;

  const parts = value.split("/");
  if (parts.length !== 2) return null;

  const numerator = parseNumber(parts[0]);
  const denominator = parseNumber(parts[1]);
  if (numerator === null || denominator === null || denominator === 0) return null;
  return numerator / denominator;
}

export function parseWholeNumber(value: string): number | null {
  const parsed = parseNumber(value);
  if (parsed === null || !Number.isInteger(parsed)) return null;
  return parsed;
}

// Accepted input formats: DD/MM/YYYY (UK default) and ISO-8601 YYYY-MM-DD.
// Excel date cells are normalised to ISO at read time.
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const UK_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

export function parseDate(value: string): number | null {
  const trimmed = value.trim();

  let year: number, month: number, day: number;
  const iso = ISO_DATE.exec(trimmed);
  const uk = UK_DATE.exec(trimmed);
  if (iso) {
    [year, month, day] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  } else if (uk) {
    [year, month, day] = [Number(uk[3]), Number(uk[2]), Number(uk[1])];
  } else {
    return null;
  }

  const timestamp = Date.UTC(year, month - 1, day);
  const roundTrip = new Date(timestamp);
  // Rejects overflow like 31/02/2026, which Date.UTC would silently roll into March.
  if (roundTrip.getUTCFullYear() !== year || roundTrip.getUTCMonth() !== month - 1 || roundTrip.getUTCDate() !== day) {
    return null;
  }
  return timestamp;
}

// Simple email check: exactly one "@" and a domain with at least one "." after it.
export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  const atIndex = trimmed.indexOf("@");
  if (atIndex <= 0 || atIndex !== trimmed.lastIndexOf("@")) return false;
  const domain = trimmed.slice(atIndex + 1);
  const dotIndex = domain.indexOf(".");
  return dotIndex > 0 && dotIndex < domain.length - 1;
}

// Phone: length ≤ 20; digits only after stripping "+", "-" and spaces.
export function isValidPhone(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length > 20) return false;
  const stripped = trimmed.replace(/[+\- ]/g, "");
  return stripped.length > 0 && /^\d+$/.test(stripped);
}

// Sort code: length ≤ 8; digits only after stripping "-".
export function isValidSortCode(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length > 8) return false;
  const stripped = trimmed.replace(/-/g, "");
  return stripped.length > 0 && /^\d+$/.test(stripped);
}
